/**
 * UN-DOLLAR RENTFLOW — ADMIN APPLICATION
 * 
 * Admin-only application with restricted routing.
 * Only admin and superadmin users can access this portal.
 */

import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import { useAuth } from './contexts/AuthContext';

// Admin views
import SignIn from './views/auth/SignIn';
import Dashboard from './views/admin/Dashboard';
import Monitoring from './views/admin/Monitoring';
import UserManagement from './views/admin/Users/UserManagement';
import ContractsPage from './pages/ContractsPage';
import ContractDetailPage from './pages/ContractDetailPage';
import ProposalsPage from './pages/ProposalsPage';
import SettingsPage from './pages/SettingsPage';
import PaymentsPage from './pages/PaymentsPage';
import { LeadList } from './views/admin/Sales/LeadList';
import { LeadDetails } from './views/admin/Sales/LeadDetails';
import { NewLead } from './views/admin/Sales/NewLead';
import { ProposalBuilder } from './views/admin/Sales/ProposalBuilder';
import { SalesMetrics } from './views/admin/Sales/SalesMetrics';
import { DemoCalendar } from './views/admin/Sales/DemoCalendar';
import { OnboardingQueue } from './views/admin/Onboarding/OnboardingQueue';
import { OnboardingDetail } from './views/admin/Onboarding/OnboardingDetail';
import { OnboardingMetrics } from './views/admin/Onboarding/OnboardingMetrics';
import { AutomationQueue } from './views/admin/Sales/AutomationQueue';
import LandlordOnboardingWorkflow from './views/admin/LandlordOnboarding/LandlordOnboardingWorkflow';
import AdminLayout from './layouts/AdminLayout';

// Admin Platform Pages
import { PropertiesPage } from './pages/PropertiesPage';
import { AssetsPage } from './pages/AssetsPage';
import { SupportTicketsPage } from './pages/SupportTicketsPage';
import { FeedbackPage } from './pages/FeedbackPage';
import { ReportsPage } from './pages/ReportsPage';
import { KnowledgeBasePage } from './pages/KnowledgeBasePage';

// Guards
import { RoleGuard } from './components/guards/RoleGuard';

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        Loading...
      </Box>
    );
  }

  return user ? <>{children}</> : <Navigate to="/auth/sign-in" replace />;
}

// Admin Only Route Component
function AdminOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin, loading, userProfile } = useAuth();

  if (loading) {
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
        Loading...
      </Box>
    );
  }

  // Allow admin, superadmin, and sales roles to access admin portal
  const hasAccess = isAdmin || userProfile?.role === 'sales';

  if (!hasAccess) {
    console.error('🚫 Access Denied - Admin check failed:', {
      isAdmin,
      userProfile,
      role: userProfile?.role
    });
    return (
      <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" flexDirection="column" gap={4}>
        <Box fontSize="xl" fontWeight="bold">Access Denied</Box>
        <Box color="gray.500">This portal is restricted to administrators and sales team only.</Box>
        <Box color="gray.400" fontSize="sm" mt={2}>Role: {userProfile?.role || 'none'}</Box>
      </Box>
    );
  }

  console.log('✅ Admin access granted:', { role: userProfile?.role });
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/auth/sign-in" element={<SignIn />} />

      {/* Protected Admin Routes */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute>
            <AdminOnlyRoute>
              <AdminLayout>
                <Routes>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="monitoring" element={<Monitoring />} />
                  <Route 
                    path="users" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin']}>
                        <UserManagement />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="contracts" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'accountant']}>
                        <ContractsPage />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="contracts/:contractId" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'accountant']}>
                        <ContractDetailPage />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="settings" 
                    element={
                      <RoleGuard allowedRoles={['superadmin']}>
                        <SettingsPage />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="payments" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'accountant']}>
                        <PaymentsPage />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="proposals" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'sales', 'sales']}>
                        <ProposalsPage />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="onboarding" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'support']}>
                        <OnboardingQueue />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="onboarding/:id" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'support']}>
                        <OnboardingDetail />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="onboarding/metrics" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'support']}>
                        <OnboardingMetrics />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="landlord-onboarding" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'support']}>
                        <LandlordOnboardingWorkflow />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="sales/automation" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'sales', 'sales']}>
                        <AutomationQueue />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="sales/metrics" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'sales', 'sales']}>
                        <SalesMetrics />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="sales/demos" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'sales', 'sales']}>
                        <DemoCalendar />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="sales/leads" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'sales', 'sales']}>
                        <LeadList />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="sales/leads/new" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'sales', 'sales']}>
                        <NewLead />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="sales/leads/:leadId" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'sales', 'sales']}>
                        <LeadDetails />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="sales/proposals/new" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'sales', 'sales']}>
                        <ProposalBuilder />
                      </RoleGuard>
                    } 
                  />
                  
                  {/* Admin Platform Routes */}
                  <Route 
                    path="properties" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin']}>
                        <PropertiesPage />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="assets" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin']}>
                        <AssetsPage />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="support/tickets" 
                    element={
                      <RoleGuard allowedRoles={['admin', 'superadmin', 'support']}>
                        <SupportTicketsPage />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="support/feedback" 
                    element={
                      <RoleGuard allowedRoles={['admin', 'superadmin', 'support']}>
                        <FeedbackPage />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="support/knowledge-base" 
                    element={
                      <RoleGuard allowedRoles={['admin', 'superadmin', 'support']}>
                        <KnowledgeBasePage />
                      </RoleGuard>
                    } 
                  />
                  <Route 
                    path="reports" 
                    element={
                      <RoleGuard allowedRoles={['superadmin', 'admin', 'support']}>
                        <ReportsPage />
                      </RoleGuard>
                    } 
                  />
                  
                  <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                </Routes>
              </AdminLayout>
            </AdminOnlyRoute>
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
    </Routes>
  );
}
